import { Participant, ParticipantForm, Vehicle, VehicleForm } from '../types/vehicle';
import { http } from './http';
import { LEGAL_FORM } from './codes';

/** 자금원 · 참여자 — /api/vehicles · /api/participants */
type VehicleRow = Omit<Vehicle, 'legalForm'> & { legalForm: string | null; [k: string]: unknown };
const toUi = (r: VehicleRow): Vehicle => ({ ...(r as any), legalForm: LEGAL_FORM.toUi(r.legalForm), note: r.note ?? '' });
const fromUi = (p: Partial<Vehicle>) => {
  const { createdAt, ...rest } = p as any;
  return 'legalForm' in p ? { ...rest, legalForm: LEGAL_FORM.toDb(p.legalForm as string | null) } : rest;
};

export const vehicleApi = {
  async list(): Promise<Vehicle[]> {
    const rows = (await http.get<VehicleRow[]>('/vehicles')).map(toUi);
    return rows.sort((a, b) => a.kind.localeCompare(b.kind) || b.startedOn.localeCompare(a.startedOn));
  },
  async get(id: number): Promise<Vehicle | undefined> {
    return toUi(await http.get<VehicleRow>(`/vehicles/${id}`));
  },
  participants(vehicleId: number): Promise<Participant[]> {
    return http.get<Participant[]>('/participants', { vehicleId });
  },
  /** 모든 자금원의 참여자 — 원장처럼 자금원을 가로질러 볼 때 */
  allParticipants(): Promise<Participant[]> {
    return http.get<Participant[]>('/participants');
  },
  async create(form: VehicleForm): Promise<Vehicle> {
    const v = toUi(await http.post<VehicleRow>('/vehicles', {
      name: form.name.trim(), kind: form.kind,
      // 빈 칸은 null — 모르는 것을 모른다고 적는다
      legalForm: LEGAL_FORM.toDb(form.legalForm), bizNo: form.bizNo.trim() || null,
      accountName: form.accountName.trim() || null, startedOn: form.startedOn, note: form.note.trim(),
    }));
    for (const p of form.participants.filter((x) => x.name.trim())) {
      await http.post('/participants', { vehicleId: v.id, name: p.name.trim(), isManager: p.isManager, committedAmount: p.committedAmount });
    }
    return v;
  },
  addParticipant(vehicleId: number, form: ParticipantForm): Promise<Participant> {
    return http.post<Participant>('/participants', { vehicleId, name: form.name.trim(), isManager: form.isManager, committedAmount: form.committedAmount });
  },
  updateParticipant(id: number, patch: Partial<Participant>): Promise<Participant> {
    const { id: _i, ...rest } = patch as any;
    return http.patch<Participant>(`/participants/${id}`, rest);
  },
  async update(id: number, patch: Partial<Vehicle>): Promise<Vehicle> {
    const { id: _i, ...rest } = fromUi(patch) as any;
    return toUi(await http.patch<VehicleRow>(`/vehicles/${id}`, rest));
  },
};
